import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { Icons, useNotification } from '@ohif/ui-next';
import AppHeader from '../../components/AppHeader/AppHeader';
import AppTooltip from '../../components/AppTooltip/AppTooltip';
import Pagination from '../../components/Pagination';

import { useAuth } from '../../contexts/AuthContext';
import AuthService from '../../services/AuthService';
import graderService from '../../services/GraderService';
import ClientService from '../../services/ClientService';
import SecureStorageService from '../../services/SecureStorageService';
import { formatNameByCountry } from '../../utils/nameFormatter';
import { applyViewerStudyOpenSession } from '../../utils/viewerStudySession';
import {
  clearGraderStudyIndexFiltersSession,
  getDefaultStudiesListFilters,
  getStudyIndicatorFlagsFromCheckboxes,
  mapStatusFilterToApi,
  persistGraderStudyIndexFilters,
  readStudiesListFiltersFromSession,
} from '../../utils/graderStudyIndexFilters';
import loadingGif from '../../../assets/loading.gif';
import pendingLegend from '../../../assets/pending-legend.png';
import inprogressLegend from '../../../assets/inprogress-legend.png';
import completedLegend from '../../../assets/completed-legend.png';
import { STUDY_STATUS_OPTIONS } from '../../constants/studiesFilters';
import '../../styles/style.css';
import './StudiesList.css';


/** Survives viewer → studiesList navigation (no location.state). */
type StudiesListNavSource = 'submissions' | 'menu' | 'direct' | 'qc';

const STUDIES_LIST_NAV_SOURCE_KEY = 'studiesListNavSource';

// Custom table component for studies
const statusIconMap = {
  pending: pendingLegend,
  'in-progress': inprogressLegend,
  'in progress': inprogressLegend,
  in_progress: inprogressLegend,
  processing: inprogressLegend,
  completed: completedLegend,
  complete: completedLegend,
  assigned: pendingLegend, // use pending icon to differentiate from in-progress
};

const normalizeStatusKey = status => (status || '').toString().trim().toLowerCase();

// Presentable label derived from API status (e.g., IN_PROGRESS -> In Progress)
const formatStatusLabel = status => {
  if (!status) return 'N/A';
  const pretty = status
    .toString()
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return pretty.replace(/\b\w/g, c => c.toUpperCase());
};

const getGraderNamesFromAssignees = (study, graders = []) => {
  const assignees = study?.assignees;
  if (!assignees || !Array.isArray(assignees) || assignees.length === 0) {
    const graderId = study?.grader_id;
    if (graderId != null) {
      const name = graders.find(g => g.id === graderId)?.displayName || `Grader ${graderId}`;
      return [name];
    }
    return [];
  }
  const uniqueIds = [...new Set(assignees.map(a => a.curr_user_id).filter(id => id != null))];
  return uniqueIds.map(id => graders.find(g => g.id === id)?.displayName || `Grader ${id}`);
};

// Use protocol.grading_slots: 1 = 2-eye (single grader), 2 = 4-eye (two graders)
const is4EyeReview = (protocol) => {
  if (!protocol) return false;
  const slots = protocol.grading_slots;
  if (slots == null) return false;
  return Number(slots) === 2;
};

// Prefer study.assignees (curr_user_id); fall back to legacy grader_id — same rules as the table column.
const getCurrentAssigneeIdsFromStudy = study => {
  const fromAssignees = (study?.assignees || [])
    .map(a => a.curr_user_id)
    .filter(id => id != null)
    .map(id => Number(id))
    .filter(id => !Number.isNaN(id));

  if (fromAssignees.length > 0) {
    return [...new Set(fromAssignees)];
  }

  if (study?.grader_id != null) {
    const graderId = Number(study.grader_id);
    if (!Number.isNaN(graderId)) {
      return [graderId];
    }
  }

  return [];
};

const getStudyOutlierFlags = study => {
  const outliers = study?.meta?.outliers;
  return {
    isExcessive: Boolean(outliers?.is_excessive),
    isTooFast: Boolean(outliers?.is_too_fast),
  };
};

const adjustGradersPopoverPosition = (popoverElement: HTMLDivElement | null) => {
  if (!popoverElement) {
    return;
  }

  requestAnimationFrame(() => {
    const rect = popoverElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const padding = 20;

    popoverElement.classList.remove('bottom-positioned', 'top-positioned', 'right-positioned');

    const wrapper = popoverElement.parentElement;
    if (!wrapper) {
      return;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const popoverHeight = popoverElement.offsetHeight;
    const verticalCenter = wrapperRect.top + wrapperRect.height / 2;
    const wouldExtendBottom = verticalCenter + popoverHeight / 2 > viewportHeight - padding;
    const wouldExtendTop = verticalCenter - popoverHeight / 2 < padding;
    const spaceAbove = wrapperRect.top;
    const spaceBelow = viewportHeight - wrapperRect.bottom;

    if (wouldExtendBottom && spaceAbove > popoverHeight) {
      popoverElement.classList.add('bottom-positioned');
    } else if (wouldExtendTop && spaceBelow > popoverHeight) {
      popoverElement.classList.add('top-positioned');
    }

    if (rect.left < padding) {
      popoverElement.classList.add('right-positioned');
    }
  });
};

const StudiesTable = ({ studies = [], graders = [], onRowClick, onGraderChange, onGraderChangeMultiple, onEscalate, isAdmin = false, isAdminOrManager = false, currentPage = 1, pageSize = 10 }) => {
  const { t } = useTranslation();
  const [openMenuStudyId, setOpenMenuStudyId] = useState(null);
  const [showAssigneeSubmenu, setShowAssigneeSubmenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [mainMenuHeight, setMainMenuHeight] = useState(80);
  const [buttonRect, setButtonRect] = useState({ top: 0, bottom: 0, left: 0, right: 0 });
  const [pendingAssigneeIds, setPendingAssigneeIds] = useState([]);
  const menuRefs = useRef({});
  const buttonRefs = useRef({});
  const gradersPopoverRefs = useRef({});

  // Removed - handleGraderChange is now called directly from the grader menu

  useEffect(() => {
    const handleClickOutside = e => {
      if (!openMenuStudyId) {
        return;
      }
      // Check if click is outside any menu elements
      const isMenuElement = e.target.closest('[data-menu="true"]');
      if (!isMenuElement) {
        setOpenMenuStudyId(null);
        setShowAssigneeSubmenu(false);
        setPendingAssigneeIds([]);
        setButtonRect({ top: 0, bottom: 0, left: 0, right: 0 });
        setMainMenuHeight(80);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuStudyId]);

  return (
    <div className="users-table-container studies-list-table-wrapper">
      <table className="users-table">
        <thead>
          <tr>
             <th className="sl-no-header table-header-noPaddingX" style={{ width: '80px' }}>Sl No</th>
             <th className="text-center" style={{ width: '160px' }}>Pid</th>
             <th className="text-noWrap text-left" style={{ width: '160px' }}>Client</th>
              <th className="text-noWrap text-left" style={{ width: '18%' }}>Protocol</th>
              <th className="text-noWrap text-left" style={{ width: '22%' }}>Instructions</th>
            <th className="text-noWrap text-center" style={{ width: '120px' }}>Start Date</th>
            <th className="text-noWrap text-center" style={{ width: '120px' }}>End Date</th>
            <th className="text-noWrap text-left" style={{ width: '140px' }}>Grader</th>
            <th className="text-noWrap" style={{ width: '110px' }}>Status</th>
            <th className="text-noWrap text-center table-header-noPaddingX" style={{ width: '100px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {studies.length === 0 ? (
            <tr>
              <td colSpan={10} style={{ textAlign: 'center', padding: '40px', color: '#cbd5e0' }}>
                No Data Found
              </td>
            </tr>
          ) : (
            studies.map((study, index) => {
              const tableIndex = (currentPage - 1) * pageSize + index + 1;
              const pidValue = study.patient_anon_id || 'N/A';
              const clientValue = study.client_name || 'N/A';
              const protocolName = study.protocol?.name || 'N/A';
              const instructionsFull = study.protocol?.description || 'N/A';
              const instructionsPreview =
                instructionsFull && instructionsFull !== 'N/A' && instructionsFull.length > 50
                  ? `${instructionsFull.substring(0, 50)}...`
                  : instructionsFull;
              const startDateDisplay = study.start_date ? moment(study.start_date).format('DD MMM YYYY') : 'N/A';
              const endDateDisplay = study.end_date ? moment(study.end_date).format('DD MMM YYYY') : 'N/A';
              const graderNames = getGraderNamesFromAssignees(study, graders);
              const statusRaw = study.status;
              const statusKey = normalizeStatusKey(statusRaw);
              const statusText = formatStatusLabel(statusRaw);
              const statusIcon = statusIconMap[statusKey];
              const isEscalated = Boolean(study.is_escalated);
              const isFlagged = Boolean(study.is_flagged);
              const isRandomSampled = Boolean(study.is_random_sampled);
              const { isExcessive, isTooFast } = getStudyOutlierFlags(study);

              return (
                <tr
                  key={study.id}
                  onClick={() => onRowClick(study)}
                >
                  <td className="text-noWrap sl-no-header" style={{ width: '80px' }}>{tableIndex}</td>
                  <td className="text-noWrap text-center" style={{ width: '160px' }}>
                    <div className="studies-pid-with-flags">
                      {isAdminOrManager &&
                        (isEscalated || isFlagged || isRandomSampled || isExcessive || isTooFast) && (
                        <div className="studies-flags-icons">
                          {isEscalated && (
                            <AppTooltip content="Escalated">
                              <span
                                className="studies-flag-icon-wrapper"
                                onClick={e => e.stopPropagation()}
                              >
                                <span className="studies-flag-icon studies-flag-icon--escalated" />
                              </span>
                            </AppTooltip>
                          )}
                          {isExcessive && (
                            <AppTooltip content="Excessive Time">
                              <span
                                className="studies-flag-icon-wrapper"
                                onClick={e => e.stopPropagation()}
                              >
                                <Icons.OutlierClock className="studies-flag-icon studies-flag-icon--excessive" />
                              </span>
                            </AppTooltip>
                          )}
                          {isTooFast && (
                            <AppTooltip content="Fast Completion">
                              <span
                                className="studies-flag-icon-wrapper"
                                onClick={e => e.stopPropagation()}
                              >
                                <Icons.OutlierBolt className="studies-flag-icon studies-flag-icon--too-fast" />
                              </span>
                            </AppTooltip>
                          )}
                          {isFlagged && (
                            <AppTooltip content="Flagged">
                              <span
                                className="studies-flag-icon-wrapper"
                                onClick={e => e.stopPropagation()}
                              >
                                <Icons.FlagPennant className="studies-flag-icon studies-flag-icon--flagged" />
                              </span>
                            </AppTooltip>
                          )}
                          {isRandomSampled && (
                            <AppTooltip content="Random sampled">
                              <span
                                className="studies-flag-icon-wrapper"
                                onClick={e => e.stopPropagation()}
                              >
                                <Icons.Dice className="studies-flag-icon studies-flag-icon--sampled" />
                              </span>
                            </AppTooltip>
                          )}
                        </div>
                      )}
                      <AppTooltip content={pidValue}>
                        <span className="studies-pid-text">
                          {pidValue}
                        </span>
                      </AppTooltip>
                    </div>
                  </td>
                  <td className="text-noWrap text-left" style={{ width: '160px' }}>
                    <AppTooltip content={clientValue}>
                      {clientValue}
                    </AppTooltip>
                  </td>
                  <td className="text-noWrap text-left" style={{ width: '20%' }}>
                    <AppTooltip content={protocolName}>
                      {protocolName}
                    </AppTooltip>
                  </td>
                  <td className="text-noWrap text-left" style={{ width: '25%' }}>
                    <AppTooltip content={instructionsFull}>
                      {instructionsPreview}
                    </AppTooltip>
                  </td>
                  <td className="text-noWrap text-center" style={{ width: '120px' }}>
                    <AppTooltip content={startDateDisplay}>
                      {startDateDisplay}
                    </AppTooltip>
                  </td>
                  <td className="text-noWrap text-center" style={{ width: '120px' }}>
                    <AppTooltip content={endDateDisplay}>
                      {endDateDisplay}
                    </AppTooltip>
                  </td>
                  <td className="text-left assigned-graders" style={{ width: '140px' }}>
                    {graderNames.length > 0 ? (
                      <div
                        className="graders-wrapper vertical"
                        onClick={e => e.stopPropagation()}
                        onMouseEnter={() =>
                          adjustGradersPopoverPosition(gradersPopoverRefs.current[study.id])
                        }
                      >
                        {graderNames.slice(0, 2).map((grader, idx) => (
                          <div
                            key={`${study.id}-grader-${grader}-${idx}`}
                            className="grader-row"
                          >
                            <span className="grader-tag">{grader}</span>
                            {idx === 1 && graderNames.length > 2 && (
                              <span className="grader-count-badge">
                                +{graderNames.length - 2}
                              </span>
                            )}
                          </div>
                        ))}
                        <div
                          className="graders-popover"
                          ref={el => {
                            if (el) {
                              gradersPopoverRefs.current[study.id] = el;
                            }
                          }}
                        >
                          <div className="popover-title">
                            ASSIGNEES ({graderNames.length})
                          </div>
                          <div className="popover-list">
                            {graderNames.map((grader, idx) => (
                              <div
                                key={`${study.id}-grader-popover-${idx}`}
                                className="grader-popover-item"
                              >
                                <span className="parameter-popover-dot"></span>
                                <span className="grader-popover-text">{grader}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="no-graders">No graders assigned</span>
                    )}
                  </td>
                  <td className="text-noWrap text-left" style={{ width: '110px' }}>
                    <AppTooltip content={statusText}>
                      <div className="legend-status">
                        {statusIcon && (
                          <img
                            className="legend-img"
                            src={statusIcon}
                            alt={`${statusText} status`}
                          />
                        )}
                        <span className="legend-label">{statusText}</span>
                      </div>
                    </AppTooltip>
                  </td>
                <td style={{ textAlign: 'center', width: '100px' }}>
                  <div
                    ref={el => {
                      if (el) {
                        menuRefs.current[study.id] = el;
                      }
                    }}
                    style={{ display: 'inline-block' }}
                  >
                    <AppTooltip content="Actions">
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setShowAssigneeSubmenu(false);
                          setPendingAssigneeIds([]);
                          const newId = openMenuStudyId === study.id ? null : study.id;

                          // Reset button position and menu height when closing menu
                          if (!newId) {
                            setButtonRect({ top: 0, bottom: 0, left: 0, right: 0 });
                            setMainMenuHeight(80);
                          }

                          if (newId) {
                            // Calculate position for portal (using fixed positioning)
                            const button = e.currentTarget;
                            const rect = button.getBoundingClientRect();
                            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
                            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

                            // Store button position for submenu calculations
                            setButtonRect({
                              top: rect.top,
                              bottom: rect.bottom,
                              left: rect.left,
                              right: rect.right
                            });

                            // Keep menu within viewport horizontally
                            const desiredLeft = rect.right - 160; // main menu width ~160
                            const clampedLeft = Math.max(8, Math.min(desiredLeft, viewportWidth - 168));

                            // Estimate menu height (main menu has 2 items: Assignee and Escalate)
                            const estimatedMenuHeight = 80; // Approximate height for 2 menu items
                            setMainMenuHeight(estimatedMenuHeight);

                            const spaceBelow = viewportHeight - rect.bottom;
                            const spaceAbove = rect.top;

                            // Determine if menu should open upward or downward
                            const shouldOpenUpward = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;

                            // Calculate top position based on direction
                            const topPosition = shouldOpenUpward
                              ? rect.top - estimatedMenuHeight - 4
                              : rect.bottom + 4;

                            setMenuPosition({
                              top: topPosition,
                              left: clampedLeft
                            });
                          }

                          setOpenMenuStudyId(newId);
                        }}
                        style={{
                          background: 'transparent',
                          color: 'white',
                          border: '1px solid #3a3f99',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          fontSize: '16px',
                          lineHeight: 1,
                          cursor: 'pointer',
                        }}
                      >
                        ⋮
                      </button>
                    </AppTooltip>

                  </div>
                </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* Portal-based context menu */}
      {openMenuStudyId && (() => {
        const study = studies.find(s => s.id === openMenuStudyId);
        const statusKey = normalizeStatusKey(study?.status);
        const isCompleted = statusKey === 'completed' || statusKey === 'complete';

        return createPortal(
          <div
            className="studies-context-menu-overlay"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 999998,
              pointerEvents: 'none',
            }}
          >
            <div
              data-menu="true"
              className="studies-context-menu-popup"
              style={{
                position: 'fixed',
                top: menuPosition.top,
                left: menuPosition.left,
                background: '#0b102f',
                border: '1px solid #3a3f99',
                borderRadius: '6px',
                minWidth: '160px',
                textAlign: 'left',
                zIndex: 999999,
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                pointerEvents: 'auto',
              }}
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
            >
            {isAdminOrManager && (
              <div
                role="menuitem"
                onClick={e => {
                  if (isCompleted) return;
                  e.stopPropagation();
                  const study = studies.find(s => s.id === openMenuStudyId);
                  if (study) {
                    setPendingAssigneeIds(getCurrentAssigneeIdsFromStudy(study));
                  }

                  setShowAssigneeSubmenu(true);
                }}
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  color: '#ffffff',
                  cursor: isCompleted ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onMouseDown={e => {
                  if (isCompleted) return;
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                Assignee
                <span >▶</span>
              </div>
            )}
            <div
              role="menuitem"
              onClick={() => {
                if (isCompleted) return;
                onEscalate(openMenuStudyId);
                setOpenMenuStudyId(null);
                setShowAssigneeSubmenu(false);
                setPendingAssigneeIds([]);
              }}
              style={{
                padding: '8px 12px',
                color: 'white',
                fontSize: '13px',
                cursor: isCompleted ? 'not-allowed' : 'pointer',
                textAlign: 'left',
              }}
              onMouseDown={e => {
                if (isCompleted) return;
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              Escalate
            </div>
          </div></div>,
          document.body
        );
      })()}

      {/* Grader Selection Menu - Shows assignees from study's protocol; 2-eye single-select, 4-eye multi-select */}
      {showAssigneeSubmenu && openMenuStudyId && (() => {
        const study = studies.find(s => s.id === openMenuStudyId);
        if (!study) {
          return null;
        }

        // Don't show submenu if study is completed
        const statusKey = normalizeStatusKey(study.status);
        const isCompleted = statusKey === 'completed' || statusKey === 'complete';
        if (isCompleted) {
          return null;
        }

        const fourEye = is4EyeReview(study.protocol);
        const assignees = study.protocol?.assignees || [];
        const jobId = study.job_id || study.id;

        // Calculate horizontal position - prefer left side, fallback to right
        const leftPosition = (menuPosition.left - 220) < 8
          ? (menuPosition.left + 160)
          : (menuPosition.left - 220);

        // Calculate vertical position for submenu so that it "touches" the main menu
        // and then grows into the available space (downwards or upwards).
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        // Estimate submenu height more accurately
        const headerHeight = fourEye ? 30 : 0; // "4-eye: select up to 2 graders" header
        const itemHeight = 40; // Height per assignee item (padding + checkbox + text)
        const applyButtonHeight = fourEye ? 50 : 0; // Apply button row if 4-eye
        const borderPadding = 12; // Top and bottom padding/borders
        const estimatedSubmenuHeight = Math.min(
          headerHeight + assignees.length * itemHeight + applyButtonHeight + borderPadding,
          280 // Max height from CSS
        );

        // Main menu geometry
        const safetyMargin = 16;
        const mainMenuTop = menuPosition.top;
        const mainMenuBottom = mainMenuTop + mainMenuHeight;

        // Available space relative to main menu
        const spaceBelow = viewportHeight - safetyMargin - mainMenuTop;
        const spaceAbove = mainMenuBottom - safetyMargin;

        let submenuTop: number;

        if (spaceBelow >= estimatedSubmenuHeight) {
          // Enough space below: anchor submenu top to main menu top, grow downward.
          submenuTop = mainMenuTop;
        } else if (spaceAbove >= estimatedSubmenuHeight) {
          // Not enough below but enough above: anchor submenu bottom to main menu bottom, grow upward.
          submenuTop = mainMenuBottom - estimatedSubmenuHeight;
        } else if (spaceBelow >= spaceAbove) {
          // Constrained viewport: more room below – still anchor at top,
          // some of the submenu may overflow but edges stay touching.
          submenuTop = Math.max(safetyMargin, mainMenuTop);
        } else {
          // More room above – anchor bottom and grow upward as much as possible.
          submenuTop = Math.max(safetyMargin, mainMenuBottom - estimatedSubmenuHeight);
        }

        const togglePending = (assigneeId) => {
          const normalizedId = Number(assigneeId);
          if (Number.isNaN(normalizedId)) {
            return;
          }
          setPendingAssigneeIds(prev => {
            const has = prev.some(id => Number(id) === normalizedId);
            if (has) {
              return prev.filter(id => Number(id) !== normalizedId);
            }
            if (prev.length >= 2) return prev;
            return [...prev, normalizedId];
          });
        };

        const handleApply4Eye = () => {
          if (pendingAssigneeIds.length === 0 || !onGraderChangeMultiple) return;
          onGraderChangeMultiple(study.id, jobId, pendingAssigneeIds);
          setOpenMenuStudyId(null);
          setShowAssigneeSubmenu(false);
          setPendingAssigneeIds([]);
        };

        return createPortal(
          <div
            data-menu="true"
            className="grader-selection-menu"
            style={{
              top: `${submenuTop}px`,
              left: `${leftPosition}px`,
            }}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
          >
            {fourEye && (
              <div className="grader-submenu-title" style={{ padding: '6px 12px', fontSize: '12px', color: '#9ca3af', borderBottom: '1px solid #3a3f99' }}>
                4-eye: select up to 2 graders
              </div>
            )}
            {assignees.length === 0 ? (
              <div className="grader-menu-item" style={{ opacity: 0.6, cursor: 'default' }}>
                <span>No assignees available</span>
              </div>
            ) : fourEye ? (
              <>
                {assignees.map(assignee => {
                  const firstName = assignee.first_name || assignee.firstName || '';
                  const lastName = assignee.last_name || assignee.lastName || '';
                  const middleName = assignee.middle_name || assignee.middleName || '';
                  const formattedName = formatNameByCountry(firstName, lastName, middleName);
                  const displayName = formattedName !== '-' ? formattedName : (assignee.name || assignee.username || '-');
                  const isChecked = pendingAssigneeIds.some(id => Number(id) === Number(assignee.id));
                  return (
                    <div
                      key={assignee.id}
                      className="grader-menu-item"
                      onClick={e => {
                        e.stopPropagation();
                        togglePending(assignee.id);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        readOnly
                      />
                      <span>{displayName}</span>
                    </div>
                  );
                })}
                <div className="grader-menu-item grader-apply-row" style={{ borderTop: '1px solid #3a3f99', marginTop: '4px', paddingTop: '8px', justifyContent: 'center' }}>
                  <button
                    type="button"
                    className="grader-apply-btn"
                    onClick={e => {
                      e.stopPropagation();
                      handleApply4Eye();
                    }}
                    disabled={pendingAssigneeIds.length === 0}
                  >
                    Apply
                  </button>
                </div>
              </>
            ) : (
              assignees.map(assignee => {
                const firstName = assignee.first_name || assignee.firstName || '';
                const lastName = assignee.last_name || assignee.lastName || '';
                const middleName = assignee.middle_name || assignee.middleName || '';
                const formattedName = formatNameByCountry(firstName, lastName, middleName);
                const displayName = formattedName !== '-' ? formattedName : (assignee.name || assignee.username || '-');
                const currentAssigneeIds = getCurrentAssigneeIdsFromStudy(study);
                const isChecked = currentAssigneeIds.some(
                  id => Number(id) === Number(assignee.id)
                );
                return (
                  <div
                    key={assignee.id}
                    className="grader-menu-item"
                    onClick={e => {
                      e.stopPropagation();
                      if (!isChecked) {
                        onGraderChange(study.id, jobId, assignee.id);
                        setOpenMenuStudyId(null);
                        setShowAssigneeSubmenu(false);
                        setPendingAssigneeIds([]);
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      readOnly
                    />
                    <span>{displayName}</span>
                  </div>
                );
              })
            )}
          </div>,
          document.body
        );
      })()}
    </div>
  );
};

// Simple Multi-Select dropdown for graders with search, chips, and checkbox list
const GraderMultiSelect = ({ graders = [], value = [], onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleId = id => {
    const exists = value.includes(id);
    const next = exists ? value.filter(v => v !== id) : [...value, id];
    onChange(next);
  };

  const clearAll = () => onChange([]);

  const filtered = graders.filter(g => g.name.toLowerCase().includes(query.toLowerCase()));

  const selectedGraders = graders.filter(g => value.includes(g.id));
  const firstTwo = selectedGraders.slice(0, 2);
  const extraCount = Math.max(0, selectedGraders.length - 2);

  return (
    <div
      ref={containerRef}
      className="ms-container"
    >
      <div
        className={`ms-control ${open ? 'open' : ''}`}
        onClick={() => setOpen(v => !v)}
      >
        <div className="ms-chips">
          {firstTwo.length === 0 && <span className="ms-placeholder">Select graders…</span>}
          {firstTwo.map(g => (
            <span
              key={g.id}
              className="ms-chip"
              onClick={e => e.stopPropagation()}
            >
              {g.name}
            </span>
          ))}
          {extraCount > 0 && <span className="ms-chip ms-count">+{extraCount}</span>}
        </div>
        <span className="ms-caret">▾</span>
      </div>
      {open && (
        <div className="ms-dropdown">
          <div className="ms-toolbar">
            <input
              className="ms-search"
              placeholder="Search…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
            <AppTooltip content="Clear">
              <button
                className="ms-clear"
                onClick={e => {
                  e.stopPropagation();
                  clearAll();
                }}
              >
                ×
              </button>
            </AppTooltip>
          </div>
          <div className="ms-list">
            {filtered.map(g => {
              const checked = value.includes(g.id);
              return (
                <label
                  key={g.id}
                  className="ms-item"
                  onClick={e => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleId(g.id)}
                  />
                  <span className="ms-item-label">{g.name}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Custom filter component
const StudiesFilter = ({
  filters,
  onFilterChange,
  onSearch,
  onClear,
  clients = [],
  graders = [],
  isAdmin = true,
  isAdminOrManager = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="search-filter-section">
      <div className="filter-controls">
        <div className="status-filter-group">
          <label htmlFor="searchInput">PID/Protocol</label>
          <input
            id="searchInput"
            type="text"
            placeholder="Search..."
            value={filters?.search || ''}
            onChange={e => onFilterChange('search', e.target.value)}
            style={{
              background: '#090c29',
              color: 'white',
              border: '1px solid #3a3f99',
              borderRadius: '4px',
              padding: '8px 12px',
              fontSize: '13px',
              minWidth: '200px',
            }}
          />
        </div>

        <div className="status-filter-group">
          <label htmlFor="clientFilter">Client</label>
          <select
            id="clientFilter"
            value={filters?.client_ids || 'all'}
            onChange={e => onFilterChange('client_ids', e.target.value)}
            style={{
              background: '#090c29',
              color: 'white',
              border: '1px solid #3a3f99',
              borderRadius: '4px',
              padding: '8px 12px',
              fontSize: '13px',
            }}
          >
            <option value="all">All</option>
            {clients.map(client => (
              <option key={client.id || client.client_id} value={client.id || client.client_id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>

        <div className="status-filter-group">
          <label htmlFor="graderFilter">Grader</label>
          <select
            id="graderFilter"
            value={filters?.grader_ids || 'all'}
            onChange={e => onFilterChange('grader_ids', e.target.value)}
            disabled={!isAdmin}
            style={{
              background: '#090c29',
              color: 'white',
              border: '1px solid #3a3f99',
              borderRadius: '4px',
              padding: '8px 12px',
              fontSize: '13px',
              opacity: isAdmin ? 1 : 0.6,
              cursor: isAdmin ? 'pointer' : 'not-allowed',
            }}
          >
            <option value="all">All</option>
            {graders.map(grader => (
              <option key={grader.id} value={grader.id}>
                {grader.displayName}
              </option>
            ))}
          </select>
        </div>

        <div className="status-filter-group">
          <label htmlFor="statusFilter">Status</label>
          <select
            id="statusFilter"
            value={filters?.status || ''}
            onChange={e => onFilterChange('status', e.target.value)}
            style={{
              background: '#090c29',
              color: 'white',
              border: '1px solid #3a3f99',
              borderRadius: '4px',
              padding: '8px 12px',
              fontSize: '13px',
            }}
          >
            {STUDY_STATUS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="status-filter-group">
          <label htmlFor="startDate">Start Date</label>
          <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
            <input
              type="text"
              value={filters?.start_date ? moment(filters.start_date).format('DD MMM YYYY') : ''}
              placeholder="dd MMM yyyy"
              readOnly
              onClick={() => {
                const picker = document.getElementById('startDatePicker');
                if (picker && 'showPicker' in picker) {
                  (picker as any).showPicker();
                }
              }}
              style={{
                background: '#090c29',
                color: 'white',
                border: '1px solid #3a3f99',
                borderRadius: '4px',
                padding: '8px 12px',
                paddingRight: '35px',
                fontSize: '13px',
                width: '100%',
                cursor: 'pointer',
              }}
            />
            <span
              onClick={() => {
                const picker = document.getElementById('startDatePicker');
                if (picker && 'showPicker' in picker) {
                  (picker as any).showPicker();
                }
              }}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                pointerEvents: 'auto',
                color: '#9ca3af',
                fontSize: '16px',
              }}
            >
              📅
            </span>
            <input
              id="startDatePicker"
              type="date"
              value={filters?.start_date || ''}
              max={filters?.end_date || ''}
              onChange={e => onFilterChange('start_date', e.target.value)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
                pointerEvents: 'none',
              }}
            />
          </div>
        </div>

        <div className="status-filter-group">
          <label htmlFor="endDate">End Date</label>
          <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
            <input
              type="text"
              value={filters?.end_date ? moment(filters.end_date).format('DD MMM YYYY') : ''}
              placeholder="dd MMM yyyy"
              readOnly
              onClick={() => {
                const picker = document.getElementById('endDatePicker');
                if (picker && 'showPicker' in picker) {
                  (picker as any).showPicker();
                }
              }}
              style={{
                background: '#090c29',
                color: 'white',
                border: '1px solid #3a3f99',
                borderRadius: '4px',
                padding: '8px 12px',
                paddingRight: '35px',
                fontSize: '13px',
                width: '100%',
                cursor: 'pointer',
              }}
            />
            <span
              onClick={() => {
                const picker = document.getElementById('endDatePicker');
                if (picker && 'showPicker' in picker) {
                  (picker as any).showPicker();
                }
              }}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                pointerEvents: 'auto',
                color: '#9ca3af',
                fontSize: '16px',
              }}
            >
              📅
            </span>
            <input
              id="endDatePicker"
              type="date"
              value={filters?.end_date || ''}
              min={filters?.start_date || ''}
              onChange={e => onFilterChange('end_date', e.target.value)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
                pointerEvents: 'none',
              }}
            />
          </div>
        </div>

        <div className="status-filter-group">
          <button
            onClick={onSearch}
            style={{
              background: '#1454D4',
              color: 'white',
              border: '1px solid #1454D4',
              borderRadius: '4px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseOver={e => {
              (e.currentTarget as HTMLElement).style.background = '#2563eb';
            }}
            onMouseOut={e => {
              (e.currentTarget as HTMLElement).style.background = '#1454D4';
            }}
          >
            Search
          </button>
        </div>

        <div className="status-filter-group">
          <button
            onClick={onClear}
            style={{
              background: 'transparent',
              color: 'white',
              border: '1px solid #3a3f99',
              borderRadius: '4px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseOver={e => {
              (e.currentTarget as HTMLElement).style.background = '#0a2a5a';
              (e.currentTarget as HTMLElement).style.borderColor = '#1454D4';
            }}
            onMouseOut={e => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
              (e.currentTarget as HTMLElement).style.borderColor = '#3a3f99';
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {isAdminOrManager && (
        <div className="indicator-filter-row">
          <button
            type="button"
            className={`indicator-filter-item indicator-filter-item--escalated ${
              filters?.indicator_escalated ? 'indicator-filter-item--active' : ''
            }`}
            onClick={() =>
              onFilterChange('indicator_escalated', !filters?.indicator_escalated)
            }
          >
            <span className="indicator-toggle-switch">
              <span className="indicator-toggle-knob" />
            </span>
            <span className="indicator-toggle-text">Escalated</span>
          </button>
          <button
            type="button"
            className={`indicator-filter-item indicator-filter-item--flagged ${
              filters?.indicator_flagged ? 'indicator-filter-item--active' : ''
            }`}
            onClick={() =>
              onFilterChange('indicator_flagged', !filters?.indicator_flagged)
            }
          >
            <span className="indicator-toggle-switch">
              <span className="indicator-toggle-knob" />
            </span>
            <span className="indicator-toggle-text">Flagged</span>
          </button>
          <button
            type="button"
            className={`indicator-filter-item indicator-filter-item--random ${
              filters?.indicator_random_sampled ? 'indicator-filter-item--active' : ''
            }`}
            onClick={() =>
              onFilterChange(
                'indicator_random_sampled',
                !filters?.indicator_random_sampled
              )
            }
          >
            <span className="indicator-toggle-switch">
              <span className="indicator-toggle-knob" />
            </span>
            <span className="indicator-toggle-text">Random Sampled</span>
          </button>

          <button
            type="button"
            className={`indicator-filter-item indicator-filter-item--missing-fields ${
              filters?.indicator_missing_fields ? 'indicator-filter-item--active' : ''
            }`}
            onClick={() =>
              onFilterChange('indicator_missing_fields', !filters?.indicator_missing_fields)
            }
          >
            <span className="indicator-toggle-switch">
              <span className="indicator-toggle-knob" />
            </span>
            <span className="indicator-toggle-text">Has missing fields</span>
          </button>

          <div className="indicator-filter-dropdown">
            <label htmlFor="outliersFilter" className="indicator-filter-dropdown-label">
              Outliers:
            </label>
            <select
              id="outliersFilter"
              className="indicator-filter-dropdown-select"
              value={filters?.outliers || 'none'}
              onChange={e => onFilterChange('outliers', e.target.value)}
            >
              <option value="none">No Filter</option>
              <option value="all">All Outliers</option>
              <option value="too_fast">Fast Completion</option>
              <option value="excessive">Excessive Time</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

// Pagination component

function StudiesList() {
  const { logout, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { from?: string; protocolName?: string } | null;
  const notification = useNotification();

    // Router state is lost when returning from the viewer (navigate with pathname only). Persist entry context
  // so "Back to Submissions" only shows when the user opened Studies from the submissions job list.
  const [studiesListNavSource, setStudiesListNavSource] = useState<StudiesListNavSource>(() => {
    const v = sessionStorage.getItem(STUDIES_LIST_NAV_SOURCE_KEY);
    if (v === 'submissions' || v === 'menu' || v === 'direct') {
      return v;
    }
    return 'direct';
  });

  useEffect(() => {
    const from = locationState?.from;
    let next: StudiesListNavSource;

    if (from === 'menu') {
      next = 'menu';
    } else if (from === 'qc') {
      next = 'qc';
    } else if (from === 'joblist' || from) {
      // dashboard, etc. (any explicit from except menu) — submissions-style chrome
      next = 'submissions';
    } else {
      const existing = sessionStorage.getItem(STUDIES_LIST_NAV_SOURCE_KEY) as StudiesListNavSource | null;
      if (existing === 'submissions' || existing === 'menu' || existing === 'direct' || existing === 'qc') {
        next = existing;
      } else {
        next = 'direct';
      }
    }

    sessionStorage.setItem(STUDIES_LIST_NAV_SOURCE_KEY, next);
    setStudiesListNavSource(next);
  }, [location.key, locationState?.from]);

  const showBackToSubmissions = studiesListNavSource === 'submissions';
  const showBackToQc = studiesListNavSource === 'qc';
  const showStudiesHeadingBlock =
    studiesListNavSource === 'submissions' || studiesListNavSource === 'direct' || studiesListNavSource === 'qc';

  // Check if user is admin (case-insensitive)
  const isAdmin = user?.role?.toLowerCase() === 'admin';

  // For some flows (like the grading viewer), the AuthService is used directly
  // to determine the current user's role (including 'manager'). To ensure we
  // stay in sync with that logic, derive the admin/manager flag from the
  // AuthService's current user instead of relying only on AuthContext typing.
  const authService = AuthService.getInstance();
  const currentAuthUser = authService.getCurrentUser();
  const currentRole = currentAuthUser?.role?.toString().toLowerCase();

  // Check if user is admin or manager (case-insensitive) based on AuthService
  const isAdminOrManager = currentRole === 'admin' || currentRole === 'manager';

  // State management
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [clients, setClients] = useState([]);
  const [graders, setGraders] = useState([]);
  const [showEscalateConfirm, setShowEscalateConfirm] = useState(false);
  const [escalateStudyId, setEscalateStudyId] = useState(null);
  const [escalateReason, setEscalateReason] = useState('');
  const [escalateReasonError, setEscalateReasonError] = useState(false);
  const [escalateCategory, setEscalateCategory] = useState('');
  const [escalateCategoryError, setEscalateCategoryError] = useState(false);
  const [escalateLoading, setEscalateLoading] = useState(false);
  const [submissionProtocolName, setSubmissionProtocolName] = useState<string>(() => sessionStorage.getItem('currentSubmissionProtocol') || '');

  const getSubmissionIdFromLocation = () => {
    const params = new URLSearchParams(location.search || '');
    const submissionId = (params.get('submission_id') || '').toString().trim();
    return submissionId || null;
  };

  const submissionContextId = getSubmissionIdFromLocation();
  const latestRequestRef = useRef(0);
  const [statusCounts, setStatusCounts] = useState({
    PENDING: 0,
    ASSIGNED: 0,
    IN_PROGRESS: 0,
    COMPLETED: 0,
  });

  // Filter state — restore from session when returning from the viewer (see graderStudyIndexFilters)
  const [filters, setFilters] = useState(() => {
    const base = readStudiesListFiltersFromSession({ isAdmin, userId: user?.id });
    const params = new URLSearchParams(location.search || '');

    const status = (params.get('status') || '').toString().trim();
    const hasMissingFieldsRaw = (params.get('has_missing_fields') || '').toString().trim().toLowerCase();
    const hasMissingFields =
      hasMissingFieldsRaw === 'true' || hasMissingFieldsRaw === '1' || hasMissingFieldsRaw === 'yes';
    const graderIdParam = (params.get('grader_id') || '').toString().trim();
    const isExcessiveRaw = (params.get('is_excessive') || '').toString().trim().toLowerCase();
    const isTooFastRaw = (params.get('is_too_fast') || '').toString().trim().toLowerCase();
    const isExcessive = isExcessiveRaw === 'true' || isExcessiveRaw === '1' || isExcessiveRaw === 'yes';
    const isTooFast = isTooFastRaw === 'true' || isTooFastRaw === '1' || isTooFastRaw === 'yes';
    const hasOutliersRaw = (params.get('has_outliers') || '').toString().trim().toLowerCase();
    const isFlaggedRaw = (params.get('is_flagged') || '').toString().trim().toLowerCase();
    const isFlagged = isFlaggedRaw === 'true' || isFlaggedRaw === '1' || isFlaggedRaw === 'yes';
    const isEscalatedRaw = (params.get('is_escalated') || '').toString().trim().toLowerCase();
    const isEscalated = isEscalatedRaw === 'true' || isEscalatedRaw === '1' || isEscalatedRaw === 'yes';

    const next = { ...base };
    if (status) {
      next.status = status;
    }
    if (hasMissingFieldsRaw) {
      next.indicator_missing_fields = hasMissingFields;
    }
    if (graderIdParam) {
      next.grader_ids = graderIdParam;
    }
    if (isFlaggedRaw) {
      next.indicator_flagged = isFlagged;
    }
    if (isEscalatedRaw) {
      next.indicator_escalated = isEscalated;
    }
    // Outliers dropdown maps to API: is_excessive / is_too_fast.
    if (isExcessiveRaw || isTooFastRaw || hasOutliersRaw) {
      next.outliers = isExcessive ? 'excessive' : isTooFast ? 'too_fast' : 'all';
    }
    return next;
  });

  // Keep submission context refresh-safe by mirroring query param into sessionStorage (used by viewer / grader APIs).
  useEffect(() => {
    const submissionId = getSubmissionIdFromLocation();
    if (submissionId) {
      sessionStorage.setItem('currentSubmissionId', submissionId);
    } else {
      sessionStorage.removeItem('currentSubmissionId');
      sessionStorage.removeItem('currentSubmissionProtocol');
      setSubmissionProtocolName('');
    }
  }, [location.search]);

  const formatCountBadge = (count: number | undefined | null) => {
    const safe = typeof count === 'number' && !Number.isNaN(count) ? count : 0;
    return String(safe);
  };

  useEffect(() => {
    if (!isAdmin && user?.id) {
      const userIdValue = user.id.toString();
      setFilters(prev => {
        if (prev.grader_ids === userIdValue) {
          return prev;
        }
        return {
          ...prev,
          grader_ids: userIdValue,
        };
      });
    }
  }, [isAdmin, user?.id]);

  useEffect(() => {
    if (locationState?.protocolName) {
      setSubmissionProtocolName(locationState.protocolName);
      sessionStorage.setItem('currentSubmissionProtocol', locationState.protocolName);
    }
  }, [locationState?.protocolName]);

  // Load clients, graders, and status counts on mount
  useEffect(() => {
    loadClients();
    loadGraders();
    loadStatusCounts();

    // Clear any stale job_id when StudiesList mounts (user navigated back from viewer)
    if (sessionStorage.getItem('currentJobId')) {
      sessionStorage.removeItem('currentJobId');
    }
  }, []);

  const prevFilterSignatureRef = useRef<string>('');
  // Single fetch effect: avoids "default load" then "filtered load" double calls.
  useEffect(() => {
    const signature = JSON.stringify({
      client_ids: filters.client_ids,
      grader_ids: filters.grader_ids,
      protocol_ids: filters.protocol_ids,
      status: filters.status,
      start_date: filters.start_date,
      end_date: filters.end_date,
      search: filters.search,
      indicator_escalated: filters.indicator_escalated,
      indicator_flagged: filters.indicator_flagged,
      indicator_random_sampled: filters.indicator_random_sampled,
      indicator_missing_fields: filters.indicator_missing_fields,
      outliers: filters.outliers,
      submission_id: getSubmissionIdFromLocation(),
    });

    const prevSignature = prevFilterSignatureRef.current;
    const filtersChanged = prevSignature !== '' && prevSignature !== signature;
    prevFilterSignatureRef.current = signature;

    // If filters changed while user is on a later page, first reset to page 1.
    if (filtersChanged && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    loadStudies();
    loadStatusCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, filters, isAdmin, user?.id, location.search]);

  // Persist applied filters for GET /grader/study/index when opening a study in the viewer
  useEffect(() => {
    persistGraderStudyIndexFilters(filters, { isAdmin, userId: user?.id });
  }, [filters, isAdmin, user?.id]);

  // (loadStudies is triggered by the unified fetch effect above)

  const loadClients = async () => {
    try {
      const clientService = ClientService.getInstance();
      const response = await clientService.getClients();

      if (response.success && response.data) {
        setClients(response.data.clients || []);
      } else {
        setClients([]);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    }
  };

  const loadGraders = async () => {
    try {
      const gradersResponse = await graderService.getGraders();
      const formattedGraders = (gradersResponse || []).map(grader => {
        const formattedName = formatNameByCountry(
          grader.first_name || grader.firstName || '',
          grader.last_name || grader.lastName || '',
          grader.middle_name || grader.middleName || '',
          'IND'
        );

        return {
          id: grader.id,
          username: grader.username,
          displayName: formattedName !== '-' ? formattedName : (grader.name || grader.username || '-')
        };
      });
      setGraders(formattedGraders);

      // Store graders in encrypted session storage for use in viewer
      try {
        const secureStorage = SecureStorageService.getInstance();
        const gradersData = JSON.stringify(formattedGraders);
        await secureStorage.setSessionItem('graders_list', gradersData);
      } catch (storageError) {
        console.warn('Failed to store graders in session storage:', storageError);
      }
    } catch (error) {
      console.error('Error loading graders:', error);
    }
  };

  const getEffectiveGraderId = () => {
    if (isAdmin) {
      return filters.grader_ids || 'all';
    }
    const userIdString = user?.id ? user.id.toString() : null;
    if (filters.grader_ids && filters.grader_ids !== 'all') {
      return filters.grader_ids;
    }
    if (userIdString) {
      return userIdString;
    }
    return 'all';
  };

  const appendSubmissionContext = (params: any) => {
    const submissionId = getSubmissionIdFromLocation();
    if (submissionId) {
      params.submission_id = submissionId;
    }
    return params;
  };

  const buildStudiesListApiParams = (overrides: Record<string, unknown> = {}) => {
    const { filters: filtersOverride, grader_id: graderIdOverride, ...rest } = overrides;
    const activeFilters = (filtersOverride ?? filters) as typeof filters;
    return appendSubmissionContext({
      grader_id: (graderIdOverride as string | undefined) ?? getEffectiveGraderId(),
      client_id: activeFilters.client_ids || 'all',
      protocol_id: activeFilters.protocol_ids || 'all',
      status: mapStatusFilterToApi(activeFilters.status),
      start_date: activeFilters.start_date || undefined,
      end_date: activeFilters.end_date || undefined,
      search: activeFilters.search || undefined,
      ...getStudyIndicatorFlagsFromCheckboxes(activeFilters),
      ...rest,
    });
  };

  const loadStatusCounts = async () => {
    try {
      const counts = await graderService.getStatusCounts(buildStudiesListApiParams());
      setStatusCounts(counts);
    } catch (error) {
      console.error('Error loading status counts:', error);
      // GraderService already falls back to zeros; keep previous values on error
    }
  };

  const loadStudies = async () => {
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    try {
      const params: any = buildStudiesListApiParams({
        page: currentPage,
        size: pageSize,
      });

      const response = await graderService.getStudies(params);
      if (requestId !== latestRequestRef.current) {
        return;
      }
      // Enrich studies with isViewOnly: use assignees (curr_user_id) — user can edit if they are in assignees
      const studiesWithAccessFlag = (response.data || []).map(study => {
        const assigneeUserIds = (study.assignees || [])
          .map(a => a.curr_user_id)
          .filter(id => id != null);
        const currentUserId = user?.id;
        const isAssignee =
          currentUserId != null && assigneeUserIds.some(id => Number(id) === Number(currentUserId));
        const isViewOnly = currentUserId != null && !isAssignee;
        return {
          ...study,
          isViewOnly,
        };
      });

      setStudies(studiesWithAccessFlag);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error('Error loading studies:', error);
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
      }
    }
  };


  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page
    loadStudies();
  };

  const handleClear = async () => {
    // Reset filters to default values (respecting admin/user grader filter)
    const defaultFilters = getDefaultStudiesListFilters({ isAdmin, userId: user?.id });

    clearGraderStudyIndexFiltersSession();

    // Update state
    setFilters(defaultFilters);
    setCurrentPage(1);

    // Immediately call API with default values instead of waiting for state update
    setLoading(true);
    try {
      const params: any = buildStudiesListApiParams({
        page: 1,
        size: pageSize,
        filters: defaultFilters,
        grader_id: isAdmin
          ? defaultFilters.grader_ids
          : user?.id
            ? user.id.toString()
            : defaultFilters.grader_ids,
      });

      const response = await graderService.getStudies(params);
      // Enrich studies with isViewOnly: use assignees (curr_user_id)
      const studiesWithAccessFlag = (response.data || []).map(study => {
        const assigneeUserIds = (study.assignees || [])
          .map(a => a.curr_user_id)
          .filter(id => id != null);
        const currentUserId = user?.id;
        const isAssignee =
          currentUserId != null && assigneeUserIds.some(id => Number(id) === Number(currentUserId));
        const isViewOnly = currentUserId != null && !isAssignee;
        return {
          ...study,
          isViewOnly,
        };
      });

      setStudies(studiesWithAccessFlag);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error('Error loading studies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = size => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page
  };

  const handleRowClick = study => {
    // Determine if this study should be opened in view-only mode for the current user
    const assigneeUserIds = (study.assignees || []).map(a => a.curr_user_id).filter(id => id != null);
    const currentUserId = user?.id;
    const isAssignee =
      currentUserId != null && assigneeUserIds.some(id => Number(id) === Number(currentUserId));

    // Check if study is completed - if so, always use review mode (view-only)
    const statusKey = normalizeStatusKey(study.status);
    const isCompleted = statusKey === 'completed' || statusKey === 'complete' || statusKey === 'reviewed';

    // For completed studies, always use review mode (isViewOnly = true)
    // For non-completed studies, use view-only if current user is not in assignees
    const isViewOnlyForUser = isCompleted ? true : (currentUserId != null && !isAssignee);

    // Navigate to OHIF viewer using the same pattern as WorkList
    // Using the longitudinal mode which has routeName: 'viewer'
    // Use the fixed study ID as requested for all patients
    const studyInstanceUID = study.study_uid;
    const query = new URLSearchParams();
    query.append('StudyInstanceUIDs', studyInstanceUID);

    const jobId = study.job_id || study.jobId || study.jobID || study.id;

    persistGraderStudyIndexFilters(filters, { isAdmin, userId: user?.id });

    if (study.study_uid) {
      // Drop cached annotation payload so the next viewer save cannot target the previous study.
      if (typeof window !== 'undefined') {
        window.annotationsData = undefined;
      }

      // Sync session mirror runs before navigate; encrypted writes continue in background.
      void applyViewerStudyOpenSession({
        studyUid: study.study_uid,
        studyId: study.id,
        jobId: jobId ?? study.id,
        isViewOnly: isViewOnlyForUser,
      }).catch(err => {
        console.error('[StudiesList] Failed to persist viewer session:', err);
      });
    } else {
      console.warn('[StudiesList] No study_uid found in study object');
    }

    // Navigate to the viewer mode with the fixed study UID
    // This will load the same DICOM study for all patients in the list
    navigate(`/viewer?${query.toString()}`);
  };

  const handleGraderChange = async (studyId, jobId, newGraderId) => {
    try {
      const result = await graderService.changeGrader(studyId, jobId, newGraderId);

      if (result.success) {
        await loadStudies();
      } else {
        console.error('Failed to reassign grader:', result.error);
      }
    } catch (error) {
      console.error('Error changing grader:', error);
    }
  };

  const handleGraderChangeMultiple = async (studyId, jobId, graderIds) => {
    if (!Array.isArray(graderIds) || graderIds.length === 0) return;
    try {
      const result = await graderService.changeGraders(studyId, jobId, graderIds);

      if (result.success) {
        await loadStudies();
      } else {
        console.error('Failed to reassign graders:', result.error);
      }
    } catch (error) {
      console.error('Error changing graders:', error);
    }
  };

  const handleEscalateClick = (studyId) => {
    setEscalateStudyId(studyId);
    setEscalateReason('');
    setEscalateReasonError(false);
    setEscalateCategory('');
    setEscalateCategoryError(false);
    setShowEscalateConfirm(true);
  };

  const handleEscalateConfirm = async () => {
    if (!escalateStudyId) return;

    // Validate that reason is provided
    if (!escalateReason.trim()) {
      setEscalateReasonError(true);
      return;
    }
    // Validate that category is selected
    if (!escalateCategory || !['CLINICAL', 'TECHNICAL', 'OTHERS'].includes(escalateCategory)) {
      setEscalateCategoryError(true);
      return;
    }

    // Close confirmation modal first
    setShowEscalateConfirm(false);
    setEscalateLoading(true);

    try {
      const result = await graderService.escalateStudy(escalateStudyId, escalateReason.trim(), escalateCategory);

      setEscalateStudyId(null);
      setEscalateReason('');
      setEscalateReasonError(false);
      setEscalateCategory('');
      setEscalateCategoryError(false);

      if (result.success) {
        // Show success toast
        notification.show({
          title: 'Successfully escalated',
          message: result.message || 'Study has been successfully escalated',
          type: 'success',
          duration: 3000,
          position: 'top-right',
        });

        // Reload studies list to reflect the change
        await loadStudies();
      } else {
        console.error('Failed to escalate study:', result.error);

        // Show error toast
        notification.show({
          title: 'Failed to escalate study',
          message: result.error || 'Please try again later',
          type: 'error',
          duration: 5000,
          position: 'top-right',
        });
      }
    } catch (error) {
      console.error('Error escalating study:', error);

      setEscalateStudyId(null);
      setEscalateReason('');
      setEscalateReasonError(false);
      setEscalateCategory('');
      setEscalateCategoryError(false);

      // Show error toast
      notification.show({
        title: 'Failed to escalate study',
        message: 'An error occurred. Please try again later',
        type: 'error',
        duration: 5000,
        position: 'top-right',
      });
    } finally {
      setEscalateLoading(false);
    }
  };

  const handleEscalateCancel = () => {
    setShowEscalateConfirm(false);
    setEscalateStudyId(null);
    setEscalateReason('');
    setEscalateReasonError(false);
    setEscalateCategory('');
    setEscalateCategoryError(false);
  };

  return (
    <div
      className="app-dark-bg main-container"
      >
      {/* style={{ minHeight: '100vh' }} */}
      <AppHeader />

      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {/* Back Button and Page Title */}
        <div
          style={{
            padding: '20px 20px 0 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {showBackToQc && (
            <button
              onClick={() => {
                const submissionId = getSubmissionIdFromLocation() || '';
                if (submissionId) {
                  navigate(`/${encodeURIComponent(submissionId)}/qc`);
                } else {
                  navigate('/submissions');
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'transparent',
                border: '1px solid #3a3f99',
                borderRadius: '6px',
                padding: '8px 16px',
                color: 'white',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseOver={e => {
                (e.currentTarget as HTMLElement).style.background = '#0a2a5a';
                (e.currentTarget as HTMLElement).style.borderColor = '#1454D4';
              }}
              onMouseOut={e => {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
                (e.currentTarget as HTMLElement).style.borderColor = '#3a3f99';
              }}
            >
              <Icons.ArrowLeft className="h-4 w-4" />
              Back to QC
            </button>
          )}
          {showBackToSubmissions && (
            <button
              onClick={() => navigate('/submissions')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'transparent',
                border: '1px solid #3a3f99',
                borderRadius: '6px',
                padding: '8px 16px',
                color: 'white',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseOver={e => {
                (e.currentTarget as HTMLElement).style.background = '#0a2a5a';
                (e.currentTarget as HTMLElement).style.borderColor = '#1454D4';
              }}
              onMouseOut={e => {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
                (e.currentTarget as HTMLElement).style.borderColor = '#3a3f99';
              }}
            >
              <Icons.ArrowLeft className="h-4 w-4" />
              Back to Submissions
            </button>
          )}
          {showStudiesHeadingBlock && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <h1
                style={{
                  color: 'white',
                  fontSize: '24px',
                  fontWeight: '600',
                  margin: 0,
                }}
              >
                Studies
              </h1>
              <p
                style={{
                  color: '#9ca3af',
                  fontSize: '14px',
                  margin: '4px 0 0 0',
                }}
              >
                {(locationState?.from === 'joblist' || !!submissionContextId) && submissionProtocolName
                  ? submissionProtocolName
                  : 'Manage and review DICOM studies for grading'}
              </p>
            </div>
          )}
          <div style={{ width: '120px' }}></div> {/* Spacer for centering */}
        </div>

        <div style={{ flex: 1, margin: '0.5rem 1.5rem' }}>
          <StudiesFilter
            filters={filters}
            onFilterChange={handleFilterChange}
            onSearch={handleSearch}
            onClear={handleClear}
            clients={clients}
            graders={graders}
            isAdmin={isAdmin}
            isAdminOrManager={isAdminOrManager}
          />

          {/* Status pills row (above table) */}
          <div className="studies-status-pills-row" role="tablist" aria-label="Study status filters">
            {STUDY_STATUS_OPTIONS.filter(option => option.value).map(option => {
              const countKey = (option.value || '').toString().toUpperCase() as
                | 'PENDING'
                | 'ASSIGNED'
                | 'IN_PROGRESS'
                | 'COMPLETED';
              const count = statusCounts[countKey] ?? 0;
              const badgeText = formatCountBadge(count);
              const isActive = (filters?.status || '') === (option.value || '');
              const statusKey = (option.value || '').toString().toUpperCase();
              const pillStatusClass =
                statusKey === 'COMPLETED'
                  ? 'status-completed'
                  : statusKey === 'ASSIGNED'
                    ? 'status-assigned'
                    : statusKey === 'IN_PROGRESS'
                      ? 'status-in-progress'
                      : statusKey === 'PENDING'
                        ? 'status-pending'
                        : 'status-all';

              return (
                <button
                  key={option.value || '__all__'}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`studies-status-pill ${pillStatusClass} ${isActive ? 'active' : ''}`}
                  onClick={() => handleFilterChange('status', option.value)}
                >
                  <span className="studies-status-pill-left">
                    <img
                      className="studies-status-pill-icon"
                      src={
                        statusKey === 'COMPLETED'
                          ? completedLegend
                          : statusKey === 'IN_PROGRESS'
                            ? inprogressLegend
                            : pendingLegend
                      }
                      alt=""
                    />
                    <span className="studies-status-pill-label">
                      {option.label.toUpperCase()}
                    </span>
                  </span>
                  <span className="studies-status-pill-badge" aria-label={`${badgeText} studies`}>
                    {badgeText}
                  </span>
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="loading-container">
              <img src={loadingGif} alt="Loading..." className="loading-gif" />
            </div>
          ) : (
            <>
              <StudiesTable
                studies={studies}
                graders={graders}
                onRowClick={handleRowClick}
                onGraderChange={handleGraderChange}
                onGraderChangeMultiple={handleGraderChangeMultiple}
                onEscalate={handleEscalateClick}
                isAdmin={isAdmin}
                isAdminOrManager={isAdminOrManager}
                currentPage={currentPage}
                pageSize={pageSize}
              />
              {studies.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={total}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Escalate Confirmation Modal */}
      {showEscalateConfirm && (
        <div className="modal-overlay top-center" role="dialog" aria-modal="true">
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Confirm Escalation</h3>
              <button
                className="admin-button secondary"
                onClick={handleEscalateCancel}
                disabled={escalateLoading}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to escalate this study? It will be reassigned to the respective manager and removed from your queue. This action cannot be undone.
              </p>
              <div style={{ marginTop: '20px' }}>
                <label
                  htmlFor="escalate-category"
                  style={{
                    display: 'block',
                    color: '#cbd5e0',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '8px',
                  }}
                >
                  Category <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  id="escalate-category"
                  value={escalateCategory}
                  onChange={(e) => {
                    setEscalateCategory(e.target.value);
                    if (escalateCategoryError && e.target.value) {
                      setEscalateCategoryError(false);
                    }
                  }}
                  disabled={escalateLoading}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#0a2a5a',
                    border: escalateCategoryError ? '1px solid #ef4444' : '1px solid #3a3f99',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    textTransform: 'capitalize',
                    marginBottom: '16px',
                  }}
                  onFocus={(e) => {
                    if (!escalateCategoryError) {
                      e.target.style.borderColor = '#1454D4';
                    }
                  }}
                  onBlur={(e) => {
                    if (!escalateCategoryError) {
                      e.target.style.borderColor = '#3a3f99';
                    }
                  }}
                >
                  <option value="" style={{ textTransform: 'capitalize' }}>Select category</option>
                  <option value="CLINICAL" style={{ textTransform: 'capitalize' }}>Clinical</option>
                  <option value="TECHNICAL" style={{ textTransform: 'capitalize' }}>Technical</option>
                  <option value="OTHERS" style={{ textTransform: 'capitalize' }}>Others</option>
                </select>
              </div>
              <div style={{ marginTop: '4px' }}>
                <label
                  htmlFor="escalate-reason"
                  style={{
                    display: 'block',
                    color: '#cbd5e0',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '8px',
                  }}
                >
                  Reason for escalation <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  id="escalate-reason"
                  value={escalateReason}
                  onChange={(e) => {
                    setEscalateReason(e.target.value);
                    if (escalateReasonError && e.target.value.trim()) {
                      setEscalateReasonError(false);
                    }
                  }}
                  placeholder="Please provide a reason for escalating this study..."
                  disabled={escalateLoading}
                  style={{
                    width: '100%',
                    minHeight: '100px',
                    padding: '12px',
                    backgroundColor: '#0a2a5a',
                    border: escalateReasonError ? '1px solid #ef4444' : '1px solid #3a3f99',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    if (!escalateReasonError) {
                      e.target.style.borderColor = '#1454D4';
                    }
                  }}
                  onBlur={(e) => {
                    if (!escalateReasonError) {
                      e.target.style.borderColor = '#3a3f99';
                    }
                  }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="admin-button secondary"
                onClick={handleEscalateCancel}
                disabled={escalateLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-button primary"
                onClick={handleEscalateConfirm}
                disabled={escalateLoading}
                style={{
                  background: '#ef4444',
                  borderColor: '#ef4444',
                  opacity: escalateLoading ? 0.6 : 1,
                }}
              >
                Escalate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Escalate Loading Overlay */}
      {escalateLoading && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="loading-container" style={{ position: 'relative', zIndex: 10001 }}>
            <img src={loadingGif} alt="Loading..." className="loading-gif" />
            <p style={{ color: '#a0aec0', marginTop: '16px', textAlign: 'center' }}>Escalating study...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudiesList;
